// ── Google Calendar ⇄ Clarity sync engine ────────────────────────────────────
// Pure-ish mapping helpers + a two-way reconcile orchestrator. The orchestrator
// is the only function that performs network writes; everything else is data
// transformation so it's easy to reason about (and unit-test) in isolation.
//
// Conflict model: last-edit-wins, compared on update timestamps. Deletions are
// honoured in both directions but we never silently drop a local event except
// when Google explicitly reports it as `cancelled` — so the engine is biased
// against data loss.

import {
  listCalendars,
  listEvents,
  insertEvent,
  patchEvent,
  deleteEvent,
} from './calendar';
import { isGoogleTaskEvent } from './googleTask';

const TRACK_KEY = 'clarity_gcalSync'; // last-known synced state (per calendar)
const uid = () => 'g' + Math.random().toString(36).slice(2, 10);

const localTz = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; }
  catch { return 'UTC'; }
};

// Map Clarity weekday indexes (0=Mon..6=Sun — matches the day-picker order,
// see taskLogic.js's _computeOccurringOn) to RFC5545 BYDAY codes.
const BYDAY = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];

// ── Date helpers ─────────────────────────────────────────────────────────────

const pad = (n) => String(n).padStart(2, '0');

function localDateOf(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function addDaysStr(dateStr, n) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(y, m - 1, d + n);
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
}

// "YYYY-MM-DD" + "HH:MM" → RFC3339 local datetime (no Z; timeZone sent alongside)
function localDateTime(dateStr, timeStr) {
  return `${dateStr}T${(timeStr || '00:00')}:00`;
}

// Pull "YYYY-MM-DD" and "HH:MM" back out of a Google start/end object.
function splitGoogleStamp(obj) {
  if (!obj) return { date: '', time: '' };
  if (obj.date) return { date: obj.date, time: '' }; // all-day
  // dateTime like 2026-06-20T12:30:00+08:00 → keep the local wall-clock parts.
  const m = String(obj.dateTime || '').match(/^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})/);
  if (!m) return { date: '', time: '' };
  return { date: m[1], time: `${m[2]}:${m[3]}` };
}

// ── Clarity event → Google event resource ────────────────────────────────────

export function toGoogle(ev) {
  const resource = {
    summary: ev.title || '(no title)',
    location: ev.location || undefined,
    description: ev.description || ev.notes || undefined,
  };

  // An event with no start time is treated as all-day (matches how the planner
  // already buckets time-less items into its all-day strip).
  const isAllDay = ev.allDay || !ev.time;
  if (isAllDay) {
    resource.start = { date: ev.date };
    // Google all-day end is exclusive → next day for a single-day event.
    resource.end = { date: addDaysStr(ev.endDate || ev.date, 1) };
  } else {
    const tz = localTz();
    const startTime = ev.time || '00:00';
    // Default to a 1-hour block when no end time is set.
    let endDate = ev.date;
    let endTime = ev.endTime;
    if (!endTime) {
      const [h, mi] = startTime.split(':').map(Number);
      const end = new Date(2000, 0, 1, h + 1, mi);
      endTime = `${pad(end.getHours())}:${pad(end.getMinutes())}`;
    }
    resource.start = { dateTime: localDateTime(ev.date, startTime), timeZone: tz };
    resource.end = { dateTime: localDateTime(endDate, endTime), timeZone: tz };
  }

  // Reminders: array of minute-offsets → popup overrides.
  if (Array.isArray(ev.reminders) && ev.reminders.length) {
    resource.reminders = {
      useDefault: false,
      overrides: ev.reminders.slice(0, 5).map((m) => ({ method: 'popup', minutes: Number(m) || 0 })),
    };
  } else {
    resource.reminders = { useDefault: true };
  }

  // Basic recurrence mapping (Daily / Weekly-by-day). Anything fancier is left
  // local-only to avoid pushing a malformed RRULE.
  const rule = recurrenceRule(ev);
  if (rule) resource.recurrence = [rule];

  // Round-trip our local-only fields so a re-import doesn't lose them.
  resource.extendedProperties = {
    private: {
      clarityColor: ev.color || '',
      clarityCategoryId: ev.categoryId || '',
      clarityNotes: ev.notes || '',
    },
  };

  return resource;
}

// A single occurrence override (date/time/endTime tweaks only — see
// CalendarTab.jsx's drag/resize handlers) merged onto its recurring master,
// producing an event-shaped object toGoogle() can turn into a PATCH resource
// for that one Google Calendar instance.
function mergeOccurrence(master, override, origDate) {
  return {
    ...master,
    date: override.date || origDate,
    time: override.time ?? master.time,
    endTime: override.endTime ?? master.endTime,
  };
}

// RRULE's UNTIL must be a UTC date-time when DTSTART is a date-time (which
// our timed recurring events always are) — see toGoogle()'s `start`/`end`.
// Use end-of-day in the browser's local time so the last occurrence's own
// start time is always still covered, then convert to UTC.
function untilStamp(dateStr) {
  const d = new Date(`${dateStr}T23:59:59`);
  const y = d.getUTCFullYear();
  const mo = pad(d.getUTCMonth() + 1);
  const da = pad(d.getUTCDate());
  const h = pad(d.getUTCHours());
  const mi = pad(d.getUTCMinutes());
  const s = pad(d.getUTCSeconds());
  return `${y}${mo}${da}T${h}${mi}${s}Z`;
}

// Two parallel recurrence models exist in the app: the legacy `repeat`/
// `repeatDays` fields (CalendarTab's Add/Edit Event form) and the newer
// `frequencyType`/`frequencyDays`/`monthDates`/`yearDates` fields (the
// quick-add popover in CalendarExtras.jsx, originally built for tasks —
// see taskLogic.js's _computeOccurringOn, which gives it priority over
// `repeat` for exactly that reason). Google only understands RRULE, so
// translate whichever one is actually set, frequencyType first.
function recurrenceRule(ev) {
  const rule = baseRecurrenceRule(ev);
  if (!rule) return rule;
  // `repeatEnd` ("until" date) currently only has UI for the frequencyType
  // model (see CalendarExtras.jsx), but honour it either way — without this,
  // every recurring event synced to Google repeated forever regardless of
  // whatever end date the user picked in Clarity.
  return ev.repeatEnd ? `${rule};UNTIL=${untilStamp(ev.repeatEnd)}` : rule;
}

function baseRecurrenceRule(ev) {
  const ft = ev.frequencyType;
  if (ft && ft !== 'once') {
    if (ft === 'daily') return 'RRULE:FREQ=DAILY';
    if (ft === 'specific' || ft === 'count') {
      const days = Array.isArray(ev.frequencyDays) && ev.frequencyDays.length
        ? ev.frequencyDays.map((d) => BYDAY[d]).filter(Boolean)
        : null;
      return days ? `RRULE:FREQ=WEEKLY;BYDAY=${days.join(',')}` : 'RRULE:FREQ=WEEKLY';
    }
    if (ft === 'monthly') {
      const days = Array.isArray(ev.monthDates) && ev.monthDates.length ? ev.monthDates : null;
      return days ? `RRULE:FREQ=MONTHLY;BYMONTHDAY=${days.join(',')}` : 'RRULE:FREQ=MONTHLY';
    }
    if (ft === 'yearly') {
      // yearDates are "month-day" strings (1-based month, no zero-pad).
      // RRULE only takes one BYMONTH/BYMONTHDAY pair, so use the first —
      // matches the single `start` date Google needs for the series anyway.
      const first = Array.isArray(ev.yearDates) && ev.yearDates[0];
      if (first) {
        const [mo, day] = first.split('-').map(Number);
        if (mo && day) return `RRULE:FREQ=YEARLY;BYMONTH=${mo};BYMONTHDAY=${day}`;
      }
      return 'RRULE:FREQ=YEARLY';
    }
    if (ft === 'interval') {
      // Every N days / weeks / months from the start date. Note: Google skips a
      // month too short for the start day (31st) where Clarity uses its last day.
      const unit = ['day', 'week', 'month'].includes(ev.intervalUnit) ? ev.intervalUnit : 'day';
      const n = Math.max(2, parseInt(ev.intervalEvery, 10) || (unit === 'month' ? 3 : 2));
      return `RRULE:FREQ=${{ day: 'DAILY', week: 'WEEKLY', month: 'MONTHLY' }[unit]};INTERVAL=${n}`;
    }
    return null;
  }

  const r = (ev.repeat || 'None').toLowerCase();
  if (r === 'daily') return 'RRULE:FREQ=DAILY';
  if (r === 'weekly') {
    const days = Array.isArray(ev.repeatDays) && ev.repeatDays.length
      ? ev.repeatDays.map((d) => BYDAY[d]).filter(Boolean)
      : null;
    return days ? `RRULE:FREQ=WEEKLY;BYDAY=${days.join(',')}` : 'RRULE:FREQ=WEEKLY';
  }
  if (r === 'monthly') return 'RRULE:FREQ=MONTHLY';
  if (r === 'yearly') return 'RRULE:FREQ=YEARLY';
  return null;
}

// ── Google event → Clarity event (partial; merged onto local) ─────────────────

export function fromGoogle(g, calendarId) {
  const start = splitGoogleStamp(g.start);
  const end = splitGoogleStamp(g.end);
  const allDay = Boolean(g.start?.date);
  const priv = g.extendedProperties?.private || {};
  const overrides = g.reminders?.overrides || [];
  return {
    title: g.summary || '(no title)',
    date: start.date,
    time: allDay ? '' : start.time,
    endTime: allDay ? '' : end.time,
    allDay,
    location: g.location || '',
    description: g.description || '',
    reminders: overrides.map((o) => Number(o.minutes)).filter((n) => !Number.isNaN(n)),
    color: priv.clarityColor || '',
    categoryId: priv.clarityCategoryId || '',
    notes: priv.clarityNotes || '',
    // Sync bookkeeping
    gcalId: g.id,
    gcalCalendarId: calendarId,
    gcalEtag: g.etag,
    gcalUpdated: g.updated,
    // listEvents() fetches with singleEvents: true, so a recurring Google event
    // arrives as N separate instances that each carry this shared id — used to
    // cascade-delete every sibling instance together (see removeEventCascade).
    gcalRecurringId: g.recurringEventId || null,
  };
}

// ── Tracking store (last-known synced state) ──────────────────────────────────

function loadTrack() {
  try { return JSON.parse(localStorage.getItem(TRACK_KEY) || 'null') || { calendarId: '', ids: {} }; }
  catch { return { calendarId: '', ids: {} }; }
}
function saveTrack(track) {
  try { localStorage.setItem(TRACK_KEY, JSON.stringify(track)); } catch {}
}
export function resetTrack() { saveTrack({ calendarId: '', ids: {} }); }

// ── Applying a finished sync onto the latest local data ──────────────────────
// runSync works on a snapshot taken when it started, and its network calls can
// take seconds. Replacing local state with its result would throw away anything
// added, edited or deleted in the meantime. These helpers apply only what the
// sync itself changed (before -> after) onto whatever is current now (latest).

const sameJson = (a, b) => JSON.stringify(a) === JSON.stringify(b);
const SYNC_FIELDS = ['gcalId', 'gcalCalendarId', 'gcalEtag', 'gcalUpdated', 'gcalRecurringId'];

export function mergeSyncedEvents(before, after, latest) {
  const beforeById = new Map(before.map((e) => [e.id, e]));
  const afterById = new Map(after.map((e) => [e.id, e]));
  const out = [];
  for (const cur of latest) {
    const was = beforeById.get(cur.id);
    if (!was) { out.push(cur); continue; }       // added while the sync ran
    const now = afterById.get(cur.id);
    if (!now) continue;                          // removed by the sync (deleted on Google)
    if (sameJson(now, was)) out.push(cur);       // the sync didn't touch it
    else if (sameJson(cur, was)) out.push(now);  // untouched here → take the synced copy
    else {
      // Edited here while the sync ran: keep the edit, take only the Google
      // bookkeeping so the next sync pushes the edit to the right event.
      const merged = { ...cur };
      for (const k of SYNC_FIELDS) if (k in now) merged[k] = now[k];
      out.push(merged);
    }
  }
  // Brand-new events imported from Google. Something deleted here while the
  // sync ran is NOT brought back (it was in `before`).
  const latestIds = new Set(latest.map((e) => e.id));
  for (const e of after) if (!beforeById.has(e.id) && !latestIds.has(e.id)) out.push(e);
  return out;
}

export function mergeSyncedOverrides(before, after, latest) {
  const out = { ...latest };
  for (const k of new Set([...Object.keys(before), ...Object.keys(after)])) {
    if (sameJson(after[k], before[k])) continue;   // the sync didn't touch it
    if (!sameJson(latest[k], before[k])) continue; // changed here meanwhile → keep that
    if (after[k] === undefined) delete out[k];
    else out[k] = after[k];
  }
  return out;
}

// ── Public: list calendars (thin re-export for the hook) ──────────────────────
export { listCalendars };

// ── The orchestrator ─────────────────────────────────────────────────────────
//
// runSync(calEvents, calendarId, occurrenceOverrides) → { events, occurrenceOverrides, stats, changed }
//   - `events`             : the next calEvents array to push into React state.
//   - `occurrenceOverrides`: the next calEventOccurrenceOverrides to push into React state.
//   - `stats`              : { imported, updated, pushed, deleted } for the UI.
//
// It performs all Google-side writes itself; the caller just stores the result.

export async function runSync(calEvents, calendarId, occurrenceOverrides = {}) {
  const isNewer = (a, b) => {
    const ta = Date.parse(a || 0) || 0;
    const tb = Date.parse(b || 0) || 0;
    return ta > tb;
  };

  const track = loadTrack();
  // If the user switched calendars, start tracking fresh for the new one.
  if (track.calendarId !== calendarId) { track.calendarId = calendarId; track.ids = {}; }

  // Pull the Google window: 90 days back, 365 forward (covers the planner views).
  const now = new Date();
  const timeMin = new Date(now.getTime() - 90 * 86400000).toISOString();
  const timeMax = new Date(now.getTime() + 365 * 86400000).toISOString();

  const googleEvents = [];
  let pageToken;
  do {
    const page = await listEvents(calendarId, { timeMin, timeMax, pageToken });
    (page?.items || []).forEach((it) => googleEvents.push(it));
    pageToken = page?.nextPageToken;
  } while (pageToken);

  // Index local events by their Google id.
  const byGcal = new Map();
  for (const ev of calEvents) if (ev.gcalId) byGcal.set(ev.gcalId, ev);

  // Index every fetched instance of a recurring series (singleEvents: true
  // hands each occurrence back as its own event, sharing recurringEventId +
  // originalStartTime) by `${masterGcalId}:${originalOccurrenceDate}`, so the
  // occurrence-override sync below can find the right instance to patch
  // without a separate API call per occurrence.
  const instanceByRecurringKey = new Map();
  for (const g of googleEvents) {
    if (g.status === 'cancelled' || !g.recurringEventId) continue;
    const orig = splitGoogleStamp(g.originalStartTime);
    if (orig.date) instanceByRecurringKey.set(`${g.recurringEventId}:${orig.date}`, g);
  }

  let next = calEvents.map((e) => ({ ...e }));
  const stats = { imported: 0, updated: 0, pushed: 0, deleted: 0 };
  const seenGoogleIds = new Set();
  const cancelledIds = new Set();

  // ── Google → local ──────────────────────────────────────────────────────────
  for (const g of googleEvents) {
    if (g.status === 'cancelled') {
      cancelledIds.add(g.id);
      // Google deleted it → remove locally if we have it.
      const idx = next.findIndex((e) => e.gcalId === g.id);
      if (idx !== -1) { next.splice(idx, 1); stats.deleted++; }
      continue;
    }
    seenGoogleIds.add(g.id);
    const mapped = fromGoogle(g, calendarId);
    const local = byGcal.get(g.id);
    if (!local) {
      // A Clarity-native recurring event we pushed up gets stored on Google as
      // one master (gcalId = master's id). Pulling with singleEvents: true
      // then hands back every occurrence as its own instance (different id,
      // recurringEventId = the master's id) — without this check each one
      // would look like a brand-new Google event and get imported as a
      // duplicate ghost copy sitting on top of the original recurring block.
      if (g.recurringEventId && byGcal.has(g.recurringEventId)) continue;
      if (track.ids[g.id]) {
        // We synced this event before, but it's no longer present locally —
        // the user deleted it in Clarity. Without this check it would look
        // indistinguishable from a brand-new Google event and get reimported,
        // resurrecting a deletion the user already made.
        try { await deleteEvent(calendarId, g.id); stats.deleted++; } catch { /* leave for next sync */ }
        delete track.ids[g.id];
        continue;
      }
      // New event coming in from Google.
      next.push({ id: uid(), repeat: 'None', repeatDays: [], repeatDates: [], ...mapped });
      stats.imported++;
    } else if (isNewer(g.updated, local.gcalUpdated || local.updatedAt)) {
      // Google's copy is newer → update our fields (keep local id + extras).
      const idx = next.findIndex((e) => e.id === local.id);
      if (idx !== -1) {
        next[idx] = { ...next[idx], ...mapped };
        stats.updated++;
      }
    } else if (isNewer(local.updatedAt, g.updated)) {
      // Local copy is newer → push our changes up.
      try {
        const res = await patchEvent(calendarId, g.id, toGoogle(local));
        const idx = next.findIndex((e) => e.id === local.id);
        if (idx !== -1 && res) {
          next[idx] = { ...next[idx], gcalEtag: res.etag, gcalUpdated: res.updated };
        }
        stats.pushed++;
      } catch { /* leave for next sync */ }
    }
    // Backfill gcalRecurringId unconditionally (not gated on "newer") so events
    // already synced before this field existed pick it up on their next sync,
    // instead of waiting for an unrelated edit to bump Google's updated time.
    if (local && local.gcalRecurringId !== mapped.gcalRecurringId) {
      const idx = next.findIndex((e) => e.id === local.id);
      if (idx !== -1) next[idx] = { ...next[idx], gcalRecurringId: mapped.gcalRecurringId };
    }
  }

  // ── Google Tasks deleted on Google ───────────────────────────────────────────
  // A Google Task (see ./googleTask.js) can't be deleted from Clarity, so the way
  // to get rid of one is to delete it in Google Tasks. Google may simply stop
  // listing it rather than send it back as `cancelled`, which would leave it here
  // forever; so a synced Google Task dated inside the fetched window that Google
  // no longer lists is dropped. Ordinary events keep the cancelled-only rule.
  {
    const winStart = localDateOf(new Date(now.getTime() - 89 * 86400000));
    const winEnd = localDateOf(new Date(now.getTime() + 364 * 86400000));
    const before = next.length;
    next = next.filter((e) => !(e.gcalId && isGoogleTaskEvent(e) && !seenGoogleIds.has(e.gcalId)
      && e.date >= winStart && e.date <= winEnd));
    stats.deleted += before - next.length;
  }

  // ── Cleanup: purge ghost duplicates from before the fix above ────────────────
  // Earlier syncs could already have imported per-occurrence duplicates of a
  // Clarity-native recurring event we'd pushed up (see the recurringEventId
  // check above). Anything whose gcalRecurringId points at another local
  // event's gcalId is one of those ghosts sitting on top of its own native
  // recurring master — drop it (and let the deletion loop below clean it off
  // Google too).
  {
    const localGcalIds = new Set(next.filter((e) => e.gcalId).map((e) => e.gcalId));
    const ghostIds = new Set(
      next.filter((e) => e.gcalRecurringId && localGcalIds.has(e.gcalRecurringId)).map((e) => e.id)
    );
    if (ghostIds.size) {
      next = next.filter((e) => !ghostIds.has(e.id));
      stats.deleted += ghostIds.size;
    }
  }

  // ── Local → Google: brand-new local events (never synced) ────────────────────
  for (const ev of next) {
    if (ev.gcalId) continue;
    if (!ev.title || !ev.date) continue; // skip incomplete drafts
    try {
      const res = await insertEvent(calendarId, toGoogle(ev));
      if (res) {
        ev.gcalId = res.id;
        ev.gcalCalendarId = calendarId;
        ev.gcalEtag = res.etag;
        ev.gcalUpdated = res.updated;
        stats.pushed++;
      }
    } catch { /* leave for next sync */ }
  }

  // ── Occurrence overrides ⇄ Google instance exceptions ────────────────────────
  // A single dragged/resized occurrence of a recurring event lives in
  // occurrenceOverrides (key `${masterId}:${originalOccurrenceDate}`),
  // entirely separate from the master event record. Google's equivalent is
  // an "instance exception" — PATCHing one expanded occurrence of a
  // recurring series detaches just that occurrence while keeping
  // recurringEventId + originalStartTime pointing back at the series.
  const nextOverrides = { ...occurrenceOverrides };
  let overridesChanged = false;
  const byGcalNow = new Map(next.filter((e) => e.gcalId).map((e) => [e.gcalId, e]));

  for (const [instKey, inst] of instanceByRecurringKey) {
    const sep = instKey.lastIndexOf(':');
    const masterGcalId = instKey.slice(0, sep);
    const origDate = instKey.slice(sep + 1);
    const master = byGcalNow.get(masterGcalId);
    if (!master) continue;

    const ovKey = `${master.id}:${origDate}`;
    const ov = nextOverrides[ovKey];
    const naturalStart = splitGoogleStamp(inst.start);
    const naturalEnd = splitGoogleStamp(inst.end);

    if (ov) {
      const wantDate = ov.date || origDate;
      const wantTime = ov.time ?? master.time;
      const wantEndTime = ov.endTime ?? master.endTime;
      const alreadyMatches = naturalStart.date === wantDate && naturalStart.time === wantTime && naturalEnd.time === wantEndTime;
      if (alreadyMatches) {
        if (ov.giId !== inst.id || ov.giUpdated !== inst.updated) {
          nextOverrides[ovKey] = { ...ov, giId: inst.id, giUpdated: inst.updated };
          overridesChanged = true;
        }
      } else if (isNewer(ov.updatedAt, inst.updated)) {
        // Local occurrence edit is newer — push it as an instance exception.
        try {
          const merged = mergeOccurrence(master, ov, origDate);
          const res = await patchEvent(calendarId, inst.id, toGoogle(merged));
          if (res) {
            nextOverrides[ovKey] = { ...ov, giId: res.id, giUpdated: res.updated };
            overridesChanged = true;
            stats.pushed++;
          }
        } catch { /* leave for next sync */ }
      } else if (isNewer(inst.updated, ov.giUpdated || ov.updatedAt)) {
        // The instance was edited directly in Google Calendar — pull it down.
        nextOverrides[ovKey] = {
          ...ov,
          date: naturalStart.date !== origDate ? naturalStart.date : undefined,
          time: naturalStart.time,
          endTime: naturalEnd.time,
          giId: inst.id,
          giUpdated: inst.updated,
        };
        overridesChanged = true;
        stats.updated++;
      }
    } else {
      // No local override yet — only create one if this instance is a real
      // exception (edited directly on Google); a plain unmodified occurrence
      // needs no override at all.
      const isException = naturalStart.date !== origDate || naturalStart.time !== (master.time || '') || naturalEnd.time !== (master.endTime || '');
      if (isException) {
        nextOverrides[ovKey] = {
          date: naturalStart.date !== origDate ? naturalStart.date : undefined,
          time: naturalStart.time,
          endTime: naturalEnd.time,
          giId: inst.id,
          giUpdated: inst.updated,
        };
        overridesChanged = true;
        stats.imported++;
      }
    }
  }

  // A single occurrence deleted directly in Google Calendar arrives as a
  // cancelled instance (status: 'cancelled', recurringEventId set). Drop any
  // matching local override — it reverts to whatever the series naturally
  // shows rather than resurrecting stale local values.
  for (const g of googleEvents) {
    if (g.status !== 'cancelled' || !g.recurringEventId) continue;
    const master = byGcalNow.get(g.recurringEventId);
    const orig = splitGoogleStamp(g.originalStartTime);
    if (!master || !orig.date) continue;
    const ovKey = `${master.id}:${orig.date}`;
    if (nextOverrides[ovKey]) { delete nextOverrides[ovKey]; overridesChanged = true; }
  }

  // ── Local deletions → Google ─────────────────────────────────────────────────
  // Any id we tracked last time, that is no longer present locally and wasn't
  // cancelled by Google, was deleted by the user here → delete it on Google.
  const presentGcal = new Set(next.filter((e) => e.gcalId).map((e) => e.gcalId));
  for (const gid of Object.keys(track.ids)) {
    if (!presentGcal.has(gid) && !cancelledIds.has(gid)) {
      try { await deleteEvent(calendarId, gid); stats.deleted++; } catch { /* ignore */ }
    }
  }

  // Rebuild + persist the tracking set from the final synced events.
  const ids = {};
  for (const e of next) if (e.gcalId) ids[e.gcalId] = e.id;
  saveTrack({ calendarId, ids });

  // `changed` lets the caller skip a redundant setState (and avoid an
  // auto-sync feedback loop) when a sync was a pure no-op.
  const changed = stats.imported + stats.updated + stats.pushed + stats.deleted > 0 || overridesChanged;
  return { events: next, occurrenceOverrides: overridesChanged ? nextOverrides : occurrenceOverrides, stats, changed };
}
